import { OrderIdentifierReference } from '@project44-models/shipment-entity';

import { EntityEnum } from '../../definitions/enums/Entity';
import { ItemTypesEnum } from '../../definitions/enums/ItemType';
import { Attributes, Identifiers } from '../../definitions/types/shipment';
import { scopedT } from '../../utils/helpers/scopedT';
import { ReferenceKeyItem } from '../ReferenceKeyItem';
import { RelatedReferenceItem } from '../RelatedReferenceItem';
import { CopyHandlerOutput } from './utils';

const t = scopedT();

export const MAX_COLLAPSED_REFERENCES = 3;

export const sectionsTitles: Record<ItemTypesEnum, string> = {
  [ItemTypesEnum.shipments]: t('itemsSection.relatedShipments'),
  [ItemTypesEnum.keys]: t('itemsSection.referenceKeys'),
  [ItemTypesEnum.orders]: t('itemsSection.relatedOrders'),
  [ItemTypesEnum.identifiers]: t('itemsSection.additionalIdentifiers'),
  [ItemTypesEnum.tags]: t('itemsSection.tags'),
};

interface ShipmentMapperFnProps {
  copyHandler: CopyHandlerOutput;
  masterShipmentId: string;
  isPubliclyShared: boolean;
  locationType: 'default' | 'expanded';
}

interface KeyMapperFnProps {
  copyHandler: CopyHandlerOutput;
}
interface OrderMapperFnProps extends ShipmentMapperFnProps {}

const shipmentMapperFn = ({
  copyHandler,
  isPubliclyShared,
  masterShipmentId,
  locationType,
}: ShipmentMapperFnProps) => {
  return ({ value, id }: Identifiers) => (
    <RelatedReferenceItem
      entity={{ type: EntityEnum.shipment, id: id || '' }}
      title={value}
      onTagClick={() => copyHandler.copySingleItem(value)}
      key={value}
      entityOpenDisabled={isPubliclyShared}
      masterShipmentId={masterShipmentId}
      locationType={locationType}
    />
  );
};

const keyMapperFn = ({ copyHandler }: KeyMapperFnProps) => {
  return ({ name, values }: Attributes) => (
    <ReferenceKeyItem
      label={name}
      data={values}
      onTagClick={copyHandler.copySingleItem}
      key={name}
    />
  );
};

const orderMapperFn = ({
  copyHandler,
  isPubliclyShared,
  masterShipmentId,
  locationType,
}: OrderMapperFnProps) => {
  return ({ orderIdentifier, orderId }: OrderIdentifierReference) => (
    <RelatedReferenceItem
      entity={{ type: EntityEnum.order, id: orderId || '' }}
      title={orderIdentifier}
      onTagClick={() => copyHandler.copySingleItem(orderIdentifier)}
      key={orderIdentifier}
      entityOpenDisabled={isPubliclyShared}
      masterShipmentId={masterShipmentId}
      locationType={locationType}
    />
  );
};

interface GetMapperFnProps extends ShipmentMapperFnProps {
  itemType: ItemTypesEnum;
}

export const getMapperFn = ({ itemType, ...props }: GetMapperFnProps) => {
  switch (itemType) {
    case ItemTypesEnum.identifiers:
      return keyMapperFn(props);
    case ItemTypesEnum.shipments:
      return shipmentMapperFn(props);
    case ItemTypesEnum.keys:
      return keyMapperFn(props);
    case ItemTypesEnum.orders:
      return orderMapperFn(props);
    case ItemTypesEnum.tags:
      return keyMapperFn(props);
    default:
      return null;
  }
};
